import { GenericEntityConstantsEntities } from "./GenericConstants";
import {
  IGenericRequestParametersTranslator,
  IPaginationParametersTranslator,
  PropertyTranslator,
} from "./GenericTranslator";
import {
  EntityConstants,
  GenericEntityOrderBy,
  IEntityMinimalModel,
  IGenericRequestParameters,
  IModelWithIntId,
  IPaginationParameters,
} from "./GenericTypes";
import { Person } from "./Person";

export const auditLogConstants: EntityConstants<AuditLog, AuditLogFilters> = {
  resource: "audit_logs",
  frontendIndexRoute: "audit_logs",
  entitySingular: "audit log",
  entityPlural: "audit logs",
  icon: "terminal",
};

export const AuditLogEventTypes = [
  "Login",
  "Logout",
  "ApiKeyCreate",
  "ApiKeySoftDelete",
  "ApiKeyRestore",
  "ApiKeyDelete",
  "EntityCreate",
  "EntityEdit",
  "EntitySoftDelete",
  "EntityRestore",
  "EntityDelete",
  "EntityVersionRestore",
  "EntityVersionRestore",
  "EntityShared",
  "EntityExported",
  "EntityFilesDownloaded",
  "EntityLocked",
  "EntityLockedAndSigned",
  "EntityUnlocked",
  "EntityUnlockedAndUnsigned",
  "EntityFilesEdit",
  "EntityFilesRemoved",
] as const;

export type AuditLogEventType = (typeof AuditLogEventTypes)[number];

export interface AuditLog extends IModelWithIntId {
  timestamp: string;
  eventId: AuditLogEventType;
  user?: Person;
  apiKeyId?: number;
  apiKey?: IEntityMinimalModel;
  entityType?: GenericEntityConstantsEntities;
  metadataMapping?: IEntityMinimalModel<string>;
  payload?: { [key: string]: string };
  message: string;
  entityIntRecords?: IEntityMinimalModel<number>[];
  entityLongRecords?: IEntityMinimalModel<number>[];
  entityStringRecords?: IEntityMinimalModel<string>[];
}

export const AuditLogSortingOptionsConsts = [
  ...GenericEntityOrderBy,
  "TIMESTAMP",
  "EVENT_ID",
  "USER",
  "API_KEY",
  "ENTITY_TYPE",
] as const;

export type AuditLogSortingOptions = (typeof AuditLogSortingOptionsConsts)[number];

type EntityIntIdsParameter = {
  [type in GenericEntityConstantsEntities]: number[];
};

type EntityStringIdsParameter = {
  [type in GenericEntityConstantsEntities]: string[];
};

export interface AuditLogFilters
  extends IPaginationParameters, IGenericRequestParameters<AuditLog, AuditLogSortingOptions> {
  timestampFrom?: string | null;
  timestampTo?: string | null;
  eventIds?: AuditLogEventType[] | null;
  userIds?: number[] | null;
  apiKeyIds?: (string | number)[] | null;
  entityTypes?: GenericEntityConstantsEntities[] | null;
  entityIntIds?: EntityIntIdsParameter | null;
  entityStringIds?: EntityStringIdsParameter | null;
  entityLongIds?: EntityIntIdsParameter | null;
  includeHierarchicalAuditLogs?: boolean | null;
}

export const AuditLogFiltersTranslator: PropertyTranslator<AuditLogFilters> = {
  ...IPaginationParametersTranslator,
  ...IGenericRequestParametersTranslator,
  searchTermIncludeNames: "records",
  timestampFrom: "Timestamp from",
  timestampTo: "Timestamp to",
  eventIds: "Event IDs",
  userIds: "User IDs",
  apiKeyIds: "API key IDs",
  entityTypes: "Content types",
  entityIntIds: "Content IDs",
  entityStringIds: "Content IDs",
  entityLongIds: "Content IDs",
  includeHierarchicalAuditLogs: "Include hierarchical audit logs",
} as const;

export const AuditLogFieldLabels: PropertyTranslator<AuditLog> = {
  id: "Audit-Log ID",
  timestamp: "Timestamp",
  eventId: "Event",
  user: "User",
  apiKeyId: "API key ID",
  apiKey: "API key",
  entityType: "Content type",
  payload: "Payload",
  message: "Message",
  entityIntRecords: "Records",
  entityLongRecords: "Records",
  entityStringRecords: "Records",
  metadataMapping: "Metadata mapping",
} as const;

export interface AuditLogSuggestions extends IEntityMinimalModel<AuditLog["id"]> {
  timestamp: string;
  eventId: AuditLogEventType;
  user?: IEntityMinimalModel<Person["id"]>;
  entityType?: GenericEntityConstantsEntities;
}

export const AuditLogSearchTermOptions = {
  searchTermIncludeNames: AuditLogFiltersTranslator["searchTermIncludeNames"],
  searchTermIncludeIds: AuditLogFiltersTranslator["searchTermIncludeIds"],
  // searchTermIncludeUuids: AuditLogFiltersTranslator["searchTermIncludeUuids"],
} as const;
